import axios, { Axios } from "axios";
import axiosRetry from "axios-retry";
import Environment from "./environment";
import { auth } from "../firebase";
import {typeCheck} from "type-check";
import assert from "assert";

const env = Environment.getInstance();
const API_HOST = env.getApiHost();


export type ProductParams = {
  title: string,
  imageUrl?: string,
};

export type ProductDatum = {
  id: string,
  title: string,
  imageUrl: string,
}

export type User = {
  displayName: string,
  email: string,
  expiresIn: string,
  idToken: string,
  kind: string,
  localId: string,
  profilePicture: string,
  refreshToken: string,
  registered: boolean,
};

export type UpdateProfileParams = {
  // email: TBD
  displayName: string,
};


class ApiService {

  private api: Axios;

  private static _instance: ApiService;

  private getBaseUrl() {
    return API_HOST;
  }

  private async getUserIdToken() {
    return await auth.currentUser?.getIdToken();
  }

  private constructor() {
    axiosRetry(axios, {
      retries: 3,
      retryDelay: (retryCount) => {
        console.log(`retry attempt: ${retryCount}`);
        return retryCount * 2000; // time interval between retries
      },
      retryCondition: async (error) => {
        if (!(error && error.response)) {
          return false;
        }
        if (error?.response?.status === 401) {
          console.log('Trying to get new token...');
          // Try to get firebase auth refresh
          const newToken = await auth.currentUser?.getIdToken(true);
          console.log('Got new token?', { newToken });
          return true;
        }
        // if retry condition is not specified, by default idempotent requests are retried
        return error.response.status === 503;
      },
    });
    this.api = axios.create({
      baseURL: this.getBaseUrl(),
      // timeout: 3000,
      // headers: {
      //   'X-Custom-Header': this.getUserIdToken();
      // }
    });
    // attach interceptors
    this.api.interceptors.response.use((response) => response,
      async (error) => {
        console.error('interceptor', { error });
        throw error;
    });
  }

  public static getInstance() {
    return this._instance || (this._instance = new this());
  }

  async loginWithCredentials(email: string, password: string) {
    const result = await this.api.post(`${API_HOST}/api/v1/account/login`, {
      email, password,
    }).catch((error) => {
      console.error({error})
      throw error?.response?.data || error;
    });
    const user = result.data.result;
    console.log('loginWithCredentials: the result is here:', { user });
    return user;
  }

  async registerWithCredentials(email: string, password: string): Promise<User> {
    const result = await this.api.post(`${API_HOST}/api/v1/account/create`, {
      email, password,
    }).catch((error) => {
      console.error({error})
      throw error?.response?.data || error;
    });
    const user = result.data.result;
    console.log('registerWithCredentials: the result is here:', { user });
    return user;
  }

  async updateProfile(idToken: string, fields: UpdateProfileParams) {
    const result = await this.api.post(`${API_HOST}/api/v1/account/update`, {
      displayName: fields.displayName,
    }, {
      headers: {
        [env.getAuthTokenHeaderKey()]: idToken,
      },
    }).catch((error) => {
      console.error({error})
      throw error?.response?.data ?? error;
    });
    const user = result.data.result;
    console.log('updateProfile: the result is here:', { user });
    return user;
  }

  async getProfile(idToken: string) {
    const result = await this.api.get(`${API_HOST}/api/v1/account`, {
      headers: {
        [env.getAuthTokenHeaderKey()]: idToken,
      },
    }).catch((error) => {
      console.error({error})
      throw error?.response?.data ?? error;
    });
    const user = result.data.result;
    console.log('getProfile: the result is here:', { user });
    return user;
  }

  async getAllCreations(idToken: string): Promise<ProductDatum[]> {
    const result = await this.api.get(`${API_HOST}/api/v1/products`, {
      headers: {
        [env.getAuthTokenHeaderKey()]: idToken,
      },
    }).catch((error) => {
      console.error({error})
      throw error?.response?.data ?? error;
    });
    const results = result.data.results as ProductDatum[];
    console.log('getAllCreations: the results are here:', { results });
    return results;
  }


  async getProductById(
    idToken: string,
    productId: string,
  ): Promise<ProductDatum> {
    assert(typeCheck('String', productId), 'productId must be string');
    const request = await this.api.get(
      `${API_HOST}/api/v1/products/${productId}`, {
      headers: {
        [env.getAuthTokenHeaderKey()]: idToken,
      },
    }).catch((error) => {
      console.error({error})
      throw error?.response?.data ?? error;
    });
    const result = request.data.result;
    console.log('getProductById: the result is here:', { result });
    return result;
  }

  async createProduct(idToken: string, product: ProductParams) {
    const request = await this.api.post(
      `${API_HOST}/api/v1/products/create`, product, {
        headers: {
          [env.getAuthTokenHeaderKey()]: idToken,
        },
      }).catch((error) => {
        console.error({error})
        throw error?.response?.data ?? error;
      });
      const result = request.data.result;
      console.log('createProduct: the result is here:', { result });
      return result;
  }

  async editProduct(idToken: string, product: ProductParams) {

    console.warn('>>>> ', {idToken, product})
    const request = await this.api.post(
      `${API_HOST}/api/v1/products/edit`, product, {
        headers: {
          [env.getAuthTokenHeaderKey()]: idToken,
        },
      }).catch((error) => {
        console.error({error})
        throw error?.response?.data ?? error;
      });
      const result = request.data.result;
      console.log('editProduct: the result is here:', { result });
      return result;
    }

  }
  export default ApiService;